import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, Layers, ListChecks, Euro, Search, ExternalLink, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "@/lib/api";

const fmtEUR = (n) => (n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
const TIPO_LABEL = {
  alojamiento: "Alojamiento", actividad: "Actividad", transfer: "Transfer",
  tren: "Tren", vuelo: "Vuelo", entradas: "Entradas", free_day: "Día libre", otro: "Otro",
};

const Kpi = ({ icon: Icon, label, value, tone }) => (
  <Card><CardContent className="flex items-center gap-3 p-4">
    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></div>
    <div><p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-xl font-bold tabular-nums">{value}</p></div>
  </CardContent></Card>
);

/**
 * Modal drill-down: listado de servicios contratados con un proveedor concreto.
 * Se abre al pulsar la columna "Serv." del ranking. Usa `/bi/proveedor-detalle`
 * que devuelve solo servicios de viajes vendidos y respeta los mismos filtros
 * del ranking (fechas + estados).
 */
function ProviderServicesDialog({ providerName, desde, hasta, open, onOpenChange }) {
  const { data, isLoading } = useQuery({
    queryKey: ["provider-detalle", providerName, desde, hasta],
    queryFn: async () => (await api.get("/bi/proveedor-detalle", {
      params: { proveedor: providerName, desde: desde || undefined, hasta: hasta || undefined },
    })).data,
    enabled: !!providerName && open,
  });
  const items = data?.items || [];
  const total = data?.totales?.gasto || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-hidden p-0" data-testid="provider-services-dialog">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-primary" />
            Servicios contratados · {providerName}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isLoading ? "Cargando…" : `${items.length} servicios · Total ${fmtEUR(total)}`}
            {(desde || hasta) && ` · ${desde || "…"} → ${hasta || "…"}`}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh]">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Sin servicios registrados con este proveedor en el rango seleccionado.</p>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-background">
                <TableRow>
                  <TableHead>Servicio</TableHead>
                  <TableHead className="w-28">Tipo</TableHead>
                  <TableHead className="w-28">Fecha</TableHead>
                  <TableHead>Viaje</TableHead>
                  <TableHead className="w-32 text-right">Gasto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="provider-services-body">
                {items.map((s, idx) => (
                  <TableRow key={`${s.trip_id}-${idx}`}>
                    <TableCell className="max-w-[240px] truncate font-medium" title={s.service}>{s.service}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-[10px] font-normal">{TIPO_LABEL[s.tipo] || s.tipo}</Badge></TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{s.fecha || "—"}</TableCell>
                    <TableCell className="max-w-[220px]">
                      {s.trip_id ? (
                        <a
                          href={`/app/trips?trip=${s.trip_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 truncate text-xs text-primary hover:underline"
                          title={`${s.viaje} · ${s.destino}`}
                        >
                          <span className="truncate">{s.viaje}</span>
                          <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{fmtEUR(s.gasto)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export const GastoProveedorTab = () => {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [q, setQ] = useState("");
  const [drillProvider, setDrillProvider] = useState(null); // { id, name } | null

  const { data, isLoading } = useQuery({
    queryKey: ["bi-gasto-proveedores", desde, hasta],
    queryFn: async () => (await api.get("/bi/gasto-proveedores", { params: { desde: desde || undefined, hasta: hasta || undefined } })).data,
  });
  const provAll = data?.por_proveedor || [];
  const tipos = data?.por_tipo || [];
  const tot = data?.totales || { gasto: 0, servicios: 0, proveedores: 0, tipos: 0 };
  // Filtro cliente-side: rápido y sin refetch al escribir.
  const prov = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return provAll;
    return provAll.filter((p) => (p.proveedor || "").toLowerCase().includes(term));
  }, [provAll, q]);
  const maxProv = Math.max(1, ...prov.map((p) => p.gasto));
  const maxTipo = Math.max(1, ...tipos.map((t) => t.gasto));

  return (
    <div className="space-y-5" data-testid="gasto-proveedor-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-muted-foreground">Gasto por proveedor, nº de servicios y tipos de servicio contratados (viajes vendidos).</p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-[11px] uppercase text-muted-foreground">Buscar proveedor</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nombre del proveedor…"
                className="w-56 pl-8"
                data-testid="gp-search"
              />
            </div>
          </div>
          <div className="space-y-1"><Label className="text-[11px] uppercase text-muted-foreground">Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" data-testid="gp-desde" /></div>
          <div className="space-y-1"><Label className="text-[11px] uppercase text-muted-foreground">Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" data-testid="gp-hasta" /></div>
        </div>
      </div>

      {isLoading ? <Skeleton className="h-24 w-full" /> : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi icon={Euro} label="Gasto total" value={fmtEUR(tot.gasto)} tone="bg-sky-100 text-sky-700" />
          <Kpi icon={ListChecks} label="Servicios" value={tot.servicios} tone="bg-emerald-100 text-emerald-700" />
          <Kpi icon={Building2} label="Proveedores" value={tot.proveedores} tone="bg-violet-100 text-violet-700" />
          <Kpi icon={Layers} label="Tipos" value={tot.tipos} tone="bg-amber-100 text-amber-700" />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card><CardContent className="p-0">
          <div className="border-b px-4 py-3 text-sm font-semibold">Gasto por proveedor</div>
          {isLoading ? <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Proveedor</TableHead>
                <TableHead className="w-20 text-center">Serv.</TableHead>
                <TableHead className="w-40 text-right">Gasto</TableHead>
              </TableRow></TableHeader>
              <TableBody data-testid="gp-proveedores-body">
                {prov.length === 0 ? <TableRow><TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">Sin datos.</TableCell></TableRow>
                : prov.map((p) => (
                  <TableRow key={p.proveedor} data-testid={`gp-prov-${p.proveedor}`}>
                    <TableCell>
                      <div className="font-medium">{p.proveedor}</div>
                      <div className="mt-1 h-1.5 rounded-full bg-sky-500" style={{ width: `${(p.gasto / maxProv) * 100}%` }} />
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 tabular-nums text-sm font-medium hover:bg-sky-100/60 hover:text-sky-800"
                        onClick={() => setDrillProvider({ name: p.proveedor })}
                        data-testid={`gp-prov-drill-${p.proveedor}`}
                        title="Ver detalle de servicios contratados"
                      >
                        {p.servicios}
                        <ExternalLink className="ml-1 h-3 w-3 opacity-60" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{fmtEUR(p.gasto)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent></Card>

        <Card><CardContent className="p-0">
          <div className="border-b px-4 py-3 text-sm font-semibold">Gasto por tipo de servicio</div>
          {isLoading ? <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead className="w-20 text-center">Serv.</TableHead>
                <TableHead className="w-40 text-right">Gasto</TableHead>
              </TableRow></TableHeader>
              <TableBody data-testid="gp-tipos-body">
                {tipos.length === 0 ? <TableRow><TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">Sin datos.</TableCell></TableRow>
                : tipos.map((t) => (
                  <TableRow key={t.tipo} data-testid={`gp-tipo-${t.tipo}`}>
                    <TableCell>
                      <Badge variant="secondary" className="font-normal">{TIPO_LABEL[t.tipo] || t.tipo}</Badge>
                      <div className="mt-1.5 h-1.5 rounded-full bg-amber-500" style={{ width: `${(t.gasto / maxTipo) * 100}%` }} />
                    </TableCell>
                    <TableCell className="text-center tabular-nums">{t.servicios}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{fmtEUR(t.gasto)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent></Card>
      </div>

      <ProviderServicesDialog
        providerName={drillProvider?.name}
        desde={desde}
        hasta={hasta}
        open={!!drillProvider}
        onOpenChange={(v) => !v && setDrillProvider(null)}
      />
    </div>
  );
};
